import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { Download, Link2, FlaskConical, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtOdds, pct, money, SPORT_META } from "@/lib/odds";
import { FollowBettorButton } from "@/components/FollowBettorButton";

const STATUS = { pending: "text-slate-300 border-slate-500/50", won: "text-emerald-400 border-emerald-500/60", lost: "text-red-400 border-red-500/60" };

export function TicketCard({ p, innerRef }) {
  return (
    <div ref={innerRef} data-testid="ticket-card" className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#26334D] bg-[#0B0E14] p-6" style={{ backgroundImage: "radial-gradient(ellipse 80% 40% at 50% -10%, rgba(16,185,129,0.25), transparent)" }}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-emerald-400"><FlaskConical size={16} /><span className="font-display text-lg font-black uppercase tracking-tight text-white">The Parlay <span className="text-emerald-400">Lab</span></span></span>
        <span data-testid="ticket-status" className={`rounded-full border px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS[p.status]}`}>{p.status === "pending" ? "Live" : p.status}</span>
      </div>
      <p className="mt-5 text-xs uppercase tracking-widest text-slate-500">{p.user_name} · {new Date(p.created_at).toLocaleDateString()}</p>
      <h1 className="font-display mt-1 text-3xl font-black uppercase leading-none tracking-tight text-white" data-testid="ticket-title">{p.title}</h1>
      <ul className="mt-5 space-y-2">
        {p.legs.map((l) => (
          <li key={l.pick_id} className="flex items-center gap-3 rounded-lg border border-[#26334D] bg-[#121824] px-3 py-2">
            <span className="h-7 w-1 rounded-full" style={{ background: SPORT_META[l.sport]?.color }} />
            <span className="min-w-0 flex-1"><p className="truncate text-[11px] text-slate-500">{l.matchup}</p><p className="truncate text-sm font-semibold text-white">{l.selection}</p></span>
            <span className="font-mono-num text-sm font-bold text-amber-400">{fmtOdds(l.odds)}</span>
            {l.result !== "pending" && (l.result === "won" ? <Check size={14} className="text-emerald-400" /> : <span className="text-xs font-bold text-red-400">✕</span>)}
          </li>
        ))}
      </ul>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[["Odds", fmtOdds(p.combined_american), "text-amber-400"], ["Hit prob", pct(p.win_probability), "text-emerald-400"], [p.status === "won" ? "Cashed" : "To win", money(p.status === "won" ? p.potential_payout : p.potential_payout), "text-white"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-[#121824] px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className={`font-mono-num text-lg font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-widest text-slate-600">{money(p.wager)} wager · {p.legs.length} legs · AI probability picks</p>
    </div>
  );
}

export default function Ticket() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const ref = useRef(null);
  useEffect(() => { api.get(`/tickets/${id}`).then((r) => setP(r.data)).catch(() => setErr("Ticket not found")); }, [id]);

  const download = async () => {
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `parlay-lab-${id.slice(0, 8)}.png`;
      a.click();
      toast.success("Image downloaded");
    } catch (e) {
      toast.error("Could not render image");
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  return (
    <main className="mx-auto flex max-w-3xl flex-col items-center px-4 py-12" data-testid="ticket-page">
      {err && <p className="text-red-400" data-testid="ticket-error">{err}</p>}
      {p && (
        <>
          <TicketCard p={p} innerRef={ref} />
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button data-testid="ticket-download-btn" onClick={download} className="btn-primary"><Download size={16} /> Download PNG</button>
            {p.bettor_id && <FollowBettorButton userId={p.bettor_id} name={p.user_name} size="lg" />}
            <button data-testid="ticket-copy-link-btn" onClick={copy} className="btn-ghost"><Link2 size={16} /> Copy link</button>
          </div>
          <p className="mt-8 text-center text-sm text-slate-500">Built with AI probability picks at The Parlay Lab. <Link to="/" className="text-emerald-400 underline">Build your own →</Link></p>
        </>
      )}
    </main>
  );
}
